import { useState } from 'react';
import { Plan } from "@/features/planner/api/planner";
import { IconButton, Card, Box } from "@mui/material";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { GetInGuide } from "./GetInGuide";
import { LocalEvents } from "./LocalEvents";
import { LocalIntelligence } from "./LocalIntelligence";
import { PackingList } from "./PackingList";
import { PhotographySpots } from "./PhotographySpots";
import { BudgetEstimate } from "./BudgetEstimate";
import { ConnectivityGuide } from "./ConnectivityGuide";
import { CoreInsights } from './CoreInsights';
import { UsefulLinks } from './UsefulLinks';
import { DestinationImagesCarousel } from './DestinationImagesCarousel';

interface PlanCarouselProps {
    plan: Plan;
}

export const PlanCarousel = ({ plan }: PlanCarouselProps) => {
    const [activeIndex, setActiveIndex] = useState(0);

    const allViews = [
        {
            id: 'get_in',
            component: <GetInGuide data={plan.get_in} />,
            hasData: !!(plan.get_in && (plan.get_in.arrival_guide || plan.get_in.step_by_step_summary?.length > 0))
        },
        {
            id: 'core_insights',
            component: <CoreInsights data={plan.core_insights} />,
            hasData: !!(plan.core_insights && plan.core_insights.length > 0)
        },
        {
            id: 'local_events',
            component: <LocalEvents events={plan.local_events} />,
            hasData: Array.isArray(plan.local_events) && plan.local_events.length > 0
        },
        {
            id: 'local_intelligence',
            component: <LocalIntelligence data={plan.local_intelligence} />,
            hasData: !!plan.local_intelligence
        },
        {
            id: 'packing_list',
            component: <PackingList data={plan.packing_recommendations} />,
            hasData: !!(plan.packing_recommendations &&
                ((Array.isArray(plan.packing_recommendations.essentials) && plan.packing_recommendations.essentials.length > 0) ||
                    (Array.isArray(plan.packing_recommendations.clothing) && plan.packing_recommendations.clothing.length > 0)))
        },
        {
            id: 'photography_spots',
            component: <PhotographySpots plan={plan} />,
            hasData: !!(plan.itinerary && Array.isArray(plan.itinerary.days) && plan.itinerary.days.length > 0)
        },
        {
            id: 'budget_estimate',
            component: <BudgetEstimate plan={plan} />,
            hasData: !!(plan.itinerary && Array.isArray(plan.itinerary.days) && plan.itinerary.days.length > 0)
        },
        {
            id: 'connectivity_guide',
            component: <ConnectivityGuide plan={plan} />,
            hasData: !!plan.connectivity
        },
        {
            id: 'useful_links',
            component: <UsefulLinks links={plan.useful_links} />,
            hasData: Array.isArray(plan.useful_links) && plan.useful_links.length > 0
        }
    ];

    const views = allViews.filter(v => v.hasData);

    if (views.length === 0) return null;

    // Ensure safe access to current view
    const currentIndex = activeIndex >= views.length ? 0 : activeIndex;
    const currentView = views[currentIndex];

    const handleNext = () => {
        setActiveIndex((prev) => (prev + 1) % views.length);
    };

    const handlePrev = () => {
        setActiveIndex((prev) => (prev - 1 + views.length) % views.length);
    };

    return (
        <Card
            sx={{
                backgroundColor: "#D5F5F6",
                borderRadius: "16px",
                padding: { xs: "24px 4px", md: "40px 60px" }, // Responsive padding
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                position: "relative",
                margin: "0 auto",
                minHeight: "400px",
                marginTop: 4,
            }}
        >
            {/* Navigation Arrows */}
            {views.length > 1 && (
                <>
                    <IconButton
                        onClick={handlePrev}
                        sx={{
                            position: "absolute",
                            left: { xs: "-0px", md: "10px" },
                            top: "50%",
                            transform: "translateY(-50%)",
                            backgroundColor: "#ff6b35",
                            color: "white",
                            width: { xs: "36px", md: "48px" },
                            height: { xs: "36px", md: "48px" },
                            zIndex: 10,
                            "&:hover": {
                                backgroundColor: "#e55a28",
                            },
                        }}
                    >
                        <MdChevronLeft style={{ fontSize: "32px" }} />
                    </IconButton>

                    <IconButton
                        onClick={handleNext}
                        sx={{
                            position: "absolute",
                            right: { xs: "-0px", md: "10px" },
                            top: "50%",
                            transform: "translateY(-50%)",
                            backgroundColor: "#ff6b35",
                            color: "white",
                            width: { xs: "36px", md: "48px" },
                            height: { xs: "36px", md: "48px" },
                            zIndex: 10,
                            "&:hover": {
                                backgroundColor: "#e55a28",
                            },
                        }}
                    >
                        <MdChevronRight style={{ fontSize: "32px" }} />
                    </IconButton>
                </>
            )}

            <Box key={currentView.id}>
                {/* Render current view */}
                {currentView.component}
            </Box>
        </Card>
    );
};
